import { branding } from './config';
import { username } from './auth';
import { CIE10_ODONTOLOGIA } from './cie10-odontologia';
import { createEncounter, loadPrescriber, savePrescriber } from './clinic-api';
import { emptyHistoria, emptyReceta, type Historia033, type RecetaAcess } from './print-docs';

export async function renderConsulta(
  stage: HTMLElement,
  clinicName: string,
  onSaved: (id: number) => void
): Promise<void> {
  const prescriber = await loadPrescriber().catch(() => ({
    nombre: username(),
    registroAcess: '',
    telefono: '',
    hasFirma: false,
  }));
  stage.innerHTML = `
    <div class="workspace">
      <div class="dash-head">
        <p class="eyebrow">ACESS-2023-0030 · HCU 033</p>
        <h1>Registrar consulta</h1>
        <p>Pega o escribe el dictado. Completa los campos de receta e historia. La firma debe ser el nombre completo, no una rúbrica.</p>
      </div>
      <p class="err" id="cerr" hidden></p>
      <form id="consulta" class="clinic-form">
        <section class="card">
          <p class="kicker">Dictado</p>
          <label>Texto clínico</label>
          <textarea name="dictado" rows="5" placeholder="Nombre, edad, alergias, motivo, CIE, medicamento DCI, dosis…"></textarea>
          <button class="btn quiet" type="button" id="map">Sugerir campos desde el dictado</button>
        </section>
        <section class="card">
          <p class="kicker">Profesional</p>
          <div class="row2">
            <div><label>Apellidos y nombres</label><input name="prescriptor" required value="${esc(prescriber.nombre)}" /></div>
            <input type="hidden" name="registro" value="NA" />
          </div>
          <label>Teléfono de contacto</label>
          <input name="telefono" value="${esc(prescriber.telefono)}" />
          <label>Firma manuscrita (nombre completo)</label>
          <canvas id="firma" width="520" height="140"></canvas>
          <button class="btn quiet" type="button" id="clear-firma">Borrar firma</button>
        </section>
        <section class="card">
          <p class="kicker">Paciente y diagnóstico (receta art. 5.b + historia)</p>
          <div class="row2">
            <div><label>Apellidos y nombres</label><input name="paciente" required /></div>
            <div><label>Edad</label><input name="edad" required placeholder="40 años / 3 años 2 meses" /></div>
          </div>
          <div class="row2">
            <div><label>Cédula</label><input name="cedula" /></div>
            <div><label>CIE-10</label>
              <input name="cie" required list="cies" />
              <datalist id="cies">${CIE10_ODONTOLOGIA.map((c) => `<option value="${esc(c.code)} ${esc(c.label)}">`).join('')}</datalist>
            </div>
          </div>
          <label>Alergias</label>
          <input name="alergias" required value="NINGUNA CONOCIDA" />
          <label>Motivo de consulta</label>
          <input name="motivo" required />
          <label>Enfermedad actual / examen</label>
          <textarea name="enfermedad" rows="3"></textarea>
        </section>
        <section class="card">
          <p class="kicker">Medicamento (DCI en primer lugar)</p>
          <div class="row2">
            <div><label>DCI / genérico</label><input name="dci" required /></div>
            <div><label>Forma farmacéutica</label><input name="forma" required placeholder="cápsula, suspensión…" /></div>
          </div>
          <div class="row2">
            <div><label>Concentración</label><input name="conc" required /></div>
            <div><label>Vía</label><input name="via" required value="oral" /></div>
          </div>
          <div class="row2">
            <div><label>Cantidad (número)</label><input name="cantN" required /></div>
            <div><label>Cantidad (letras)</label><input name="cantL" required /></div>
          </div>
          <div class="row2">
            <div><label>Dosis</label><input name="dosis" required /></div>
            <div><label>Frecuencia</label><input name="frec" required placeholder="cada 8 horas" /></div>
          </div>
          <label>Duración</label>
          <input name="dur" required placeholder="7 días" />
          <label>Indicaciones al paciente</label>
          <textarea name="indicaciones" rows="2"></textarea>
          <label>Signos de alarma</label>
          <input name="alarma" />
          <label>Recomendaciones no farmacológicas</label>
          <input name="nofarma" />
        </section>
        <button class="btn primary" type="submit">Guardar receta e historia y abrir PDF</button>
      </form>
    </div>`;

  const canvas = stage.querySelector('#firma') as HTMLCanvasElement;
  const strokes = attachSignature(canvas);
  stage.querySelector('#clear-firma')?.addEventListener('click', () => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokes.count = 0;
  });
  stage.querySelector('#map')?.addEventListener('click', () => mapDictado(stage));
  stage.querySelector('#consulta')?.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const err = stage.querySelector('#cerr') as HTMLElement;
    err.hidden = true;
    try {
      if (strokes.count < 40) {
        throw new Error('La firma es demasiado corta. Escribe el nombre completo (no una rúbrica).');
      }
      const form = ev.target as HTMLFormElement;
      const data = new FormData(form);
      const firma = canvas.toDataURL('image/png');
      const receta = buildReceta(clinicName, data);
      const historia = buildHistoria(clinicName, data, receta);
      await savePrescriber({
        nombre: String(data.get('prescriptor') || ''),
        registroAcess: String(data.get('registro') || ''),
        telefono: String(data.get('telefono') || ''),
        firmaPngBase64: firma,
      });
      const saved = await createEncounter({
        dictadoRaw: String(data.get('dictado') || ''),
        prescriptorNombre: String(data.get('prescriptor') || ''),
        registroAcess: String(data.get('registro') || ''),
        telefonoPrescriptor: String(data.get('telefono') || ''),
        firmaPngBase64: firma,
        receta,
        historia,
      });
      onSaved(saved.id);
    } catch (e) {
      err.hidden = false;
      err.textContent = e instanceof Error ? e.message : String(e);
    }
  });
}

function buildReceta(clinic: string, data: FormData): RecetaAcess {
  const dci = String(data.get('dci') || '');
  const dosis = String(data.get('dosis') || '');
  const frec = String(data.get('frec') || '');
  const via = String(data.get('via') || '');
  const dur = String(data.get('dur') || '');
  const rx = emptyReceta(clinic, branding.city);
  rx.pacienteApellidosNombres = String(data.get('paciente') || '');
  rx.edad = String(data.get('edad') || '');
  rx.diagnosticoCie = String(data.get('cie') || '');
  rx.alergias = String(data.get('alergias') || '');
  rx.indicaciones = String(data.get('indicaciones') || `${dci}, ${dosis}, ${frec}, vía ${via}, ${dur}`);
  rx.signosAlarma = String(data.get('alarma') || '');
  rx.recomendacionesNoFarmacologicas = String(data.get('nofarma') || '');
  rx.medicamentos = [
    {
      dci,
      formaFarmaceutica: String(data.get('forma') || ''),
      concentracion: String(data.get('conc') || ''),
      via,
      cantidadNumero: String(data.get('cantN') || ''),
      cantidadLetras: String(data.get('cantL') || ''),
      dosis,
      frecuencia: frec,
      duracion: dur,
    },
  ];
  return rx;
}

function buildHistoria(clinic: string, data: FormData, receta: RecetaAcess): Historia033 {
  const h = emptyHistoria(clinic);
  h.apellidosNombres = receta.pacienteApellidosNombres;
  h.cedula = String(data.get('cedula') || '');
  h.edad = receta.edad;
  h.alergias = receta.alergias;
  h.motivoConsulta = String(data.get('motivo') || '');
  h.enfermedadActual = String(data.get('enfermedad') || data.get('dictado') || '');
  h.diagnosticos = [{ cie: receta.diagnosticoCie, descripcion: receta.diagnosticoCie, preDef: 'DEF' }];
  h.prescripciones = receta.medicamentos
    .map((m) => `${m.dci} ${m.concentracion} ${m.formaFarmaceutica} ${m.dosis} ${m.frecuencia} ${m.duracion}`)
    .join('; ');
  return h;
}

function mapDictado(root: Element): void {
  const form = root.querySelector('#consulta') as HTMLFormElement;
  const text = String(new FormData(form).get('dictado') || '');
  const set = (name: string, value: string, overwrite = false) => {
    const el = form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement | null;
    if (!el || (!overwrite && el.value.trim())) return;
    el.value = value;
  };
  const cie = CIE10_ODONTOLOGIA.find((c) => text.toUpperCase().includes(c.code.replace('.', '')) || text.toUpperCase().includes(c.code))
    || CIE10_ODONTOLOGIA.find((c) => text.toLowerCase().includes(c.label.split(' ')[0].toLowerCase()));
  if (cie) set('cie', `${cie.code} ${cie.label}`);
  if (/ninguna/i.test(text)) set('alergias', 'NINGUNA CONOCIDA', true);
  const age = text.match(/(\d+\s*años(?:\s+\d+\s*meses)?)/i);
  if (age) set('edad', age[1]);
  const who = text.match(/paciente\s+([A-Za-zÁÉÍÓÚÑáéíóúñ]+(?:\s+[A-Za-zÁÉÍÓÚÑáéíóúñ]+){1,3})/i);
  if (who) set('paciente', who[1].toUpperCase());
  const motivo = text.match(/motivo[:\s]+([^.;]+)/i);
  if (motivo) set('motivo', motivo[1].trim());
  const dci = text.match(/\b(amoxicilina|ibuprofeno|paracetamol|metronidazol|clindamicina|nimesulida|ketorolaco|azitro?micina)\b/i);
  if (dci) set('dci', dci[1].toUpperCase());
}

function attachSignature(canvas: HTMLCanvasElement): { count: number } {
  const ctx = canvas.getContext('2d');
  const state = { count: 0, drawing: false };
  if (!ctx) return state;
  ctx.strokeStyle = '#12263a';
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const pos = (ev: PointerEvent) => {
    const r = canvas.getBoundingClientRect();
    return { x: (ev.clientX - r.left) * (canvas.width / r.width), y: (ev.clientY - r.top) * (canvas.height / r.height) };
  };
  canvas.addEventListener('pointerdown', (ev) => {
    state.drawing = true;
    const p = pos(ev);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    canvas.setPointerCapture(ev.pointerId);
  });
  canvas.addEventListener('pointermove', (ev) => {
    if (!state.drawing) return;
    const p = pos(ev);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    state.count += 1;
  });
  canvas.addEventListener('pointerup', () => { state.drawing = false; });
  return state;
}

function esc(value: string): string {
  return String(value || '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]!));
}
